import axios from "axios";
import React, { useState } from "react";
import { Link } from "react-router-dom";

import "./CreateAccountPage.css";
import { ApiConnectionString } from "../../constants/ApiConntection";
import LoadingSpinner from "../../components/LoadingSpinner";
import { ProjectName } from "../../constants/ProjectName";
import PublicLayout from "../../components/public/PublicLayout";

const logo = "/logo192.png";
const publicSiteUrl = "https://therapydiary.app";

export default function CreateAccountPage() {
  const [practiceName, setPracticeName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [country] = useState("UK");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [agree, setAgree] = useState(false);

  const [validated, setValidated] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [complete, setComplete] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const passwordMatch =
    password && confirm && password === confirm;

  const validateEmail = (value) => {
    const cleanEmail = value.trim().toLowerCase();

    if (!cleanEmail) {
      return "Enter your email address";
    }

    if (cleanEmail.includes(" ")) {
      return "Email cannot contain spaces";
    }

    const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    if (!basicEmailRegex.test(cleanEmail)) {
      return "Enter a valid email address";
    }

    const [localPart, domain] = cleanEmail.split("@");

    const commonDomainFixes = {
      "outlook.ocm": "outlook.com",
      "hotmail.ocm": "hotmail.com",
      "gmail.ocm": "gmail.com",
      "yahoo.ocm": "yahoo.com",
      "icloud.ocm": "icloud.com",
      "live.ocm": "live.com",

      "outlook.con": "outlook.com",
      "hotmail.con": "hotmail.com",
      "gmail.con": "gmail.com",
      "yahoo.con": "yahoo.com",
      "icloud.con": "icloud.com",
      "live.con": "live.com",

      "outlook.co": "outlook.com",
      "hotmail.co": "hotmail.com",
      "gmail.co": "gmail.com",
      "yahoo.co": "yahoo.com",
      "icloud.co": "icloud.com",
      "live.co": "live.com",
    };

    if (commonDomainFixes[domain]) {
      return `Did you mean ${localPart}@${commonDomainFixes[domain]}?`;
    }

    if (domain.endsWith(".ocm")) {
      return `Did you mean ${localPart}@${domain.replace(
        ".ocm",
        ".com"
      )}?`;
    }

    if (domain.endsWith(".con")) {
      return `Did you mean ${localPart}@${domain.replace(
        ".con",
        ".com"
      )}?`;
    }

    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setValidated(true);

    const form = e.currentTarget;
    const cleanEmail = email.trim().toLowerCase();
    const emailValidationMessage = validateEmail(cleanEmail);

    setEmailError(emailValidationMessage);

    if (
      !form.checkValidity() ||
      !passwordMatch ||
      emailValidationMessage
    ) {
      return;
    }

    setIsLoading(true);

    try {
      const createPendingAppUser = {
        practiceName: practiceName.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: cleanEmail,
        password,
        country,
      };

      const pendingAccResponse = await axios.post(
        `${ApiConnectionString}/api/PendingAppUser`,
        createPendingAppUser
      );

      if (pendingAccResponse.status === 200) {
        setComplete(true);
        setShowEmailModal(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <PublicLayout
        prompt="Already have an account?"
        actionText="Log in"
        actionTo="/login"
      >
        <main className="signup-loading">
          <LoadingSpinner />
        </main>
      </PublicLayout>
    );
  }

  if (complete) {
    return (
      <PublicLayout
        prompt="Already verified?"
        actionText="Log in"
        actionTo="/login"
      >
        <main className="signup-page">
          <div className="signup-success-card">
            <div className="signup-brand">
              <span className="signup-brand-mark">
                <img
                  src={logo}
                  alt={`${ProjectName} logo`}
                />
              </span>

              <div className="signup-brand-text">
                <strong>{ProjectName}</strong>
                <small>Simple practice management</small>
              </div>
            </div>

            <div className="signup-success-icon">✓</div>

            <span className="signup-pill">
              30-day free trial
            </span>

            <h1>
              Verify your email to activate your free trial
            </h1>

            <p>
              Welcome
              {firstName
                ? `, ${firstName} ${lastName}`
                : ""}
              . Please verify your email address before logging
              in.
            </p>

            <div className="signup-trial-box">
              <strong>Your free trial is nearly ready</strong>

              <p>
                Once your email is verified, you can use{" "}
                {ProjectName} free for 30 days. After that, choose
                £11.99 per month or £119 per year if you decide to
                subscribe.
              </p>
            </div>

            <div className="signup-email-warning">
              <strong>Check your email</strong>

              <p>
                We have sent a verification email to{" "}
                <b>{email}</b>.
              </p>

              <p className="signup-spam-message">
                Can't see it in your inbox? Please check your{" "}
                <strong>Junk or Spam folder.</strong>
              </p>
            </div>

            <Link
              to="/login"
              className="btn signup-btn w-100"
            >
              Go to login
            </Link>
          </div>

          {showEmailModal && (
            <div className="signup-modal-backdrop">
              <div
                className="signup-email-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="email-modal-title"
              >
                <div className="signup-modal-icon">✉</div>

                <h2 id="email-modal-title">
                  Check your email
                </h2>

                <p>
                  We've sent a verification email to:
                </p>

                <strong className="signup-modal-email">
                  {email}
                </strong>

                <div className="signup-modal-warning">
                  <strong>
                    Can't see the email in your inbox?
                  </strong>

                  <p>
                    Please check your{" "}
                    <b>Junk or Spam folder.</b> Verification
                    emails can sometimes be filtered there.
                  </p>
                </div>

                <button
                  type="button"
                  className="btn signup-btn w-100"
                  onClick={() => setShowEmailModal(false)}
                >
                  I've checked my email
                </button>
              </div>
            </div>
          )}
        </main>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout
      prompt="Already have an account?"
      actionText="Log in"
      actionTo="/login"
    >
      <main className="signup-page">
        <div className="signup-shell">
          <section className="signup-hero">
            <span className="signup-pill">
              30-day free trial
            </span>

            <h1>
              Start your 30-day <em>free trial</em>
            </h1>

            <p className="signup-hero-text">
              Simple diary software for managing appointments,
              clients, availability and client self-booking.
            </p>

            <div className="signup-price-card">
              <div className="signup-price-icon">
                <img
                  src={logo}
                  alt={`${ProjectName} logo`}
                />
              </div>

              <div>
                <strong>30 days free</strong>
                <span>Then £11.99/month or £119/year</span>
                <small>Save £24.88 with yearly billing</small>
                <small>No payment required today</small>
              </div>
            </div>

            <div className="signup-benefits">
              <div>
                <span>✓</span>
                <p>Manage appointments with ease</p>
              </div>

              <div>
                <span>✓</span>
                <p>
                  Keep client information organised in one
                  place
                </p>
              </div>

              <div>
                <span>✓</span>
                <p>Set availability and stay in control</p>
              </div>

              <div>
                <span>✓</span>
                <p>Offer client self-booking when you choose</p>
              </div>
            </div>

            <div className="signup-mini-cards">
              <div>
                <strong>Made for therapists</strong>
                <p>Designed around day-to-day practice.</p>
              </div>

              <div>
                <strong>Simple setup</strong>
                <p>Get started in minutes.</p>
              </div>

              <div>
                <strong>No pressure</strong>
                <p>Choose whether to subscribe later.</p>
              </div>
            </div>
          </section>

          <section className="signup-card">
            <div className="signup-brand">
              <span className="signup-brand-mark">
                <img
                  src={logo}
                  alt={`${ProjectName} logo`}
                />
              </span>

              <div className="signup-brand-text">
                <strong>{ProjectName}</strong>
                <small>Simple practice management</small>
              </div>
            </div>

            <div className="signup-heading">
              <h2>Create your trial account</h2>

              <p>
                No payment required today. Your trial starts
                after email verification.
              </p>
            </div>

            <form
              className={
                validated ? "was-validated" : ""
              }
              onSubmit={submit}
              noValidate
            >
              <div className="mb-3">
                <label
                  className="form-label"
                  htmlFor="practice-name"
                >
                  Practice name
                </label>

                <input
                  id="practice-name"
                  className="form-control signup-input"
                  value={practiceName}
                  onChange={(e) =>
                    setPracticeName(e.target.value)
                  }
                  placeholder="e.g. Greenfield Therapy"
                  autoComplete="organization"
                  required
                />

                <div className="invalid-feedback">
                  Enter your practice name
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label
                    className="form-label"
                    htmlFor="first-name"
                  >
                    First name
                  </label>

                  <input
                    id="first-name"
                    className="form-control signup-input"
                    value={firstName}
                    onChange={(e) =>
                      setFirstName(e.target.value)
                    }
                    autoComplete="given-name"
                    required
                  />

                  <div className="invalid-feedback">
                    Enter your first name
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <label
                    className="form-label"
                    htmlFor="last-name"
                  >
                    Last name
                  </label>

                  <input
                    id="last-name"
                    className="form-control signup-input"
                    value={lastName}
                    onChange={(e) =>
                      setLastName(e.target.value)
                    }
                    autoComplete="family-name"
                    required
                  />

                  <div className="invalid-feedback">
                    Enter your last name
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label
                  className="form-label"
                  htmlFor="signup-email"
                >
                  Email address
                </label>

                <input
                  id="signup-email"
                  type="email"
                  className={`form-control signup-input ${
                    emailError ? "is-invalid" : ""
                  }`}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);

                    if (validated || emailError) {
                      setEmailError(
                        validateEmail(e.target.value)
                      );
                    }
                  }}
                  onBlur={() =>
                    setEmailError(validateEmail(email))
                  }
                  autoComplete="email"
                  required
                />

                <div className="invalid-feedback">
                  {emailError || "Enter a valid email"}
                </div>
              </div>

              <div className="mb-3 signup-password">
                <label
                  className="form-label"
                  htmlFor="signup-password"
                >
                  Password
                </label>

                <div className="signup-password-wrap">
                  <input
                    id="signup-password"
                    type={
                      showPassword ? "text" : "password"
                    }
                    className="form-control signup-input"
                    value={password}
                    onChange={(e) =>
                      setPassword(e.target.value)
                    }
                    autoComplete="new-password"
                    required
                    minLength={6}
                  />

                  <button
                    type="button"
                    className="signup-show-btn"
                    onClick={() =>
                      setShowPassword((current) => !current)
                    }
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>

                <div className="invalid-feedback">
                  Enter a password of at least 6 characters
                </div>
              </div>

              <div className="mb-3 signup-password">
                <label
                  className="form-label"
                  htmlFor="confirm-password"
                >
                  Confirm password
                </label>

                <div className="signup-password-wrap">
                  <input
                    id="confirm-password"
                    type={
                      showConfirm ? "text" : "password"
                    }
                    className={`form-control signup-input ${
                      confirm
                        ? passwordMatch
                          ? "is-valid"
                          : "is-invalid"
                        : ""
                    }`}
                    value={confirm}
                    onChange={(e) =>
                      setConfirm(e.target.value)
                    }
                    autoComplete="new-password"
                    required
                  />

                  <button
                    type="button"
                    className="signup-show-btn"
                    onClick={() =>
                      setShowConfirm((current) => !current)
                    }
                  >
                    {showConfirm ? "Hide" : "Show"}
                  </button>
                </div>

                <div className="invalid-feedback">
                  Passwords must match
                </div>
              </div>

              <label className="signup-check">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) =>
                    setAgree(e.target.checked)
                  }
                  required
                />

                <span>
                  I agree to the Terms and Conditions, including
                  the Data Processing Agreement, and acknowledge
                  the Privacy Policy.
                </span>
              </label>

              <div className="signup-links">
                <a
                  href={`${publicSiteUrl}/privacy.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>

                {" | "}

                <a
                  href={`${publicSiteUrl}/terms.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms and Conditions
                </a>

                {" | "}

                <a
                  href={`${publicSiteUrl}/dpa.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Data Processing Agreement
                </a>
              </div>

              <button
                type="submit"
                className="btn signup-btn w-100"
                disabled={isLoading}
              >
                {isLoading
                  ? "Creating..."
                  : "Create account and start trial"}
              </button>

              <div className="signup-footer">
                <span>Already have an account?</span>{" "}
                <Link to="/login">Log in</Link>
              </div>
            </form>
          </section>
        </div>
      </main>
    </PublicLayout>
  );
}